import { Typography } from "components/typography";
import { defaultTheme } from "@mindlercare/ui-web/src/shared/constants";
import { IcbtTasks } from "api/types";
import { useGetAllProgressForUsers } from "api/useIcbtQueries";
import {
  FeedbackModalStack,
  ModalState,
} from "components/patients/patientCard/icbt/modals/FeedbackModal/FeedbackModalStack";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { unique } from "remeda";
import { styled } from "styled-components";
import { type Categories } from "../hooks/useCategorizedTodoCommunication";
import { useIcbtLanguage } from "../hooks/useTreatment";
import { RouterOutput } from "@mindlercare/mindlerapi-client";
import { useGetAllProgramsAndTreatmentsQuery } from "../../../hooks/icbt/useGetAllProgramsAndTreatmentsQuery";
import { QuestionnaireCounts } from "hooks/icbt/useGetQuestionnaireCountsPerProgramQuery";
import { parseAsStringLiteral, useQueryState } from "nuqs";
import {
  TaskItem,
  HandlerProps,
  type UserAnswersByRequestIdByUserId,
} from "./TaskItem";
import {
  categoriesForTasks,
  CategoriesForTasks,
  useSortedIcbtTodoTasks,
} from "../hooks/useSortedIcbtTodoTasks";
import EditFeedbackDateModal, {
  EditFeedbackDateModalProps,
} from "components/patients/patientCard/icbt/modals/EditFeedbackDateModal";
import { TasksTitles } from "./TasksTitles";
import { ExpandableSection } from "@mindlercare/ui-web";
import { Toggle } from "@mindlercare/ui-web/src/lib/expandableSection/Toggle";

const Categories = styled.div`
  display: flex;
  margin-bottom: 20px;
  gap: 8px;
`;

const Category = styled.div<{ $active: boolean }>`
  padding: 10px 20px;
  cursor: pointer;
  color: ${(props) =>
    props.$active
      ? defaultTheme.COLORS.GRAY_WHITE
      : defaultTheme.COLORS.GRAY_BLACK};
  border: ${(props) =>
    props.$active
      ? `2px solid ${defaultTheme.COLORS.GRAY_BLACK}`
      : `1px solid ${defaultTheme.COLORS.GRAY_MEDIUM}`};
  background-color: ${(props) =>
    props.$active
      ? defaultTheme.COLORS.GRAY_BLACK
      : defaultTheme.COLORS.GRAY_WHITE};
  border-radius: 100px;
  &:hover {
    color: ${defaultTheme.COLORS.GRAY_WHITE};
    background-color: ${defaultTheme.COLORS.GRAY_BLACK};
  }
`;

const FeedbackList = styled.ul`
  display: flex;
  flex-direction: column;
  padding: 0;
`;

const NoFeedbackMessage = styled(Typography)`
  color: ${({ theme }) => theme.COLORS.GRAY_DARK};
  padding: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.COLORS.GRAY_MEDIUM};
`;

export const TodoTasksTable = ({
  icbtTasks,
  patients,
  questionnaireCounts,
  userAnswersByRequestIdByUserId,
}: {
  icbtTasks?: IcbtTasks[];
  patients?: { firstName: string | null; lastName: string | null }[];
  questionnaireCounts: QuestionnaireCounts;
  userAnswersByRequestIdByUserId: UserAnswersByRequestIdByUserId;
}) => {
  const { t } = useTranslation();

  const [modalPropsState, setModalPropsState] = useState<ModalState>({
    programId: "",
    treatmentId: "",
    userId: undefined,
    icbtRequestId: "",
    icbtFeedbackDate: "",
    icbtFeedbackDateIgnored: false,
    patientName: "",
    feedbackModalOpen: false,
    assignIcbtModalOpen: false,
  });
  const [editFeedbackDateOpen, setEditFeedbackDateOpen] = useState<Pick<
    EditFeedbackDateModalProps,
    "intialDate" | "requestId" | "patientUserId"
  > | null>(null);
  const [tab, setTabState] = useQueryState(
    "state",
    parseAsStringLiteral(categoriesForTasks).withDefault("now"),
  );

  const handleTabChange = (newTab: CategoriesForTasks) => setTabState(newTab);

  const { data: progressByUser } = useGetAllProgressForUsers(
    unique(icbtTasks?.map((icbt) => icbt.patientUserId) ?? []),
  );
  const language = useIcbtLanguage();

  // TODO: loading state and error handling plz
  const { data: programsAndTreatments } = useGetAllProgramsAndTreatmentsQuery(
    language.codeISO2,
  );

  const categories = {
    now: t("DUE_TODAY"),
    thisWeek: t("DUE_THIS_WEEK"),
    all: t("ALL"),
  };

  const noFeedbackMessages = {
    now: t("NO_FEEDBACK_TODAY"),
    thisWeek: t("NO_FEEDBACK"),
    all: t("NO_FEEDBACK"),
  };

  const toBeDoneCategorized = useSortedIcbtTodoTasks({
    icbtTasks,
    patientsToIncludeInCommunication:
      (patients as RouterOutput["patients"]["getByUserIds"]) ?? [],
  });
  const tasksData = toBeDoneCategorized.get(tab);

  const writeTasks =
    tasksData?.filter((item) =>
      item.tasks.some((task) => task.type !== "extend_due_date"),
    ) ?? [];
  const extendDueDateTasks =
    tasksData?.filter((item) =>
      item.tasks.some((task) => task.type === "extend_due_date"),
    ) ?? [];
  const noTasks = tasksData?.filter((item) => item.tasks.length === 0) ?? [];

  const groupedTasksData = [
    {
      key: "write",
      title: t("TASKS_TITLE.WRITE"),
      amount: writeTasks.length,
      items: writeTasks,
    },
    {
      key: "extend",
      title: t("TASKS_TITLE.INACTIVE"),
      amount: extendDueDateTasks.length,
      items: extendDueDateTasks,
    },
    {
      key: "noTask",
      title: t("TASKS_TITLE.FUTURE"),
      amount: null,
      items: noTasks,
    },
  ];

  return (
    <>
      <Categories>
        {Object.entries(categories).map(([key, label]) => (
          <Category
            key={key}
            $active={tab === key}
            onClick={() => handleTabChange(key as CategoriesForTasks)}
          >
            {label}{" "}
            {toBeDoneCategorized.get(key as CategoriesForTasks)?.length ?? ""}
          </Category>
        ))}
      </Categories>
      {tasksData?.length === 0 && (
        <NoFeedbackMessage variant="body">
          {noFeedbackMessages[tab] || t("NO_FEEDBACK")}
        </NoFeedbackMessage>
      )}
      {groupedTasksData.map((group) => {
        if (group.items.length === 0) return null;

        return (
          <ExpandableSection expanded={true} key={group.key}>
            <Toggle>
              <Typography insensitive variant="h4">
                {group.title}{" "}
              </Typography>
              {group.amount ? (
                <TypographyWithBefore
                  variant="body"
                  color="GRAY_DARK"
                  insensitive
                >
                  {t("TASKS_TITLE.ACTIONS", { amount: group.amount })}
                </TypographyWithBefore>
              ) : null}
            </Toggle>
            <ExpandableSection.Content>
              <FeedbackList>
                <TasksTitles />
                {group.items.map((item, i) => (
                  <TaskItem
                    displayDropdownUpwards={
                      group.items.length > 1 && group.items.length === i + 1
                    }
                    key={item.icbtRequestId}
                    item={item}
                    done={false} // since we are in todo they can't be done
                    progress={
                      progressByUser?.[item.patientUserId]?.[item.programId]
                    }
                    programsAndTreatments={programsAndTreatments}
                    questionnaireCounts={questionnaireCounts}
                    userAnswersByRequestIdByUserId={
                      userAnswersByRequestIdByUserId
                    }
                    onClick={({ icbtTasks }: HandlerProps) => {
                      setModalPropsState({
                        icbtRequestId: icbtTasks.icbtRequestId,
                        icbtFeedbackDate: icbtTasks.feedbackDate,
                        icbtFeedbackDateIgnored: icbtTasks.feedbackDateIgnored,
                        patientName: `${icbtTasks.patient?.firstName} ${icbtTasks.patient?.lastName}`,
                        programId: icbtTasks.programId,
                        treatmentId: icbtTasks.treatmentId,
                        userId: Number(icbtTasks.patientUserId),
                        feedbackModalOpen: true,
                      });
                    }}
                    editFeedbackDateFn={() =>
                      setEditFeedbackDateOpen({
                        requestId: item.icbtRequestId,
                        patientUserId: Number(item.patientUserId),
                        intialDate: new Date(item.feedbackDate),
                      })
                    }
                  />
                ))}
              </FeedbackList>
            </ExpandableSection.Content>
          </ExpandableSection>
        );
      })}
      <FeedbackModalStack
        modalState={modalPropsState}
        setModalState={setModalPropsState}
      />
      {editFeedbackDateOpen ? (
        <EditFeedbackDateModal
          intialDate={editFeedbackDateOpen.intialDate}
          requestId={editFeedbackDateOpen.requestId}
          close={() => setEditFeedbackDateOpen(null)}
          patientUserId={editFeedbackDateOpen.patientUserId}
        />
      ) : null}
    </>
  );
};

const TypographyWithBefore = styled(Typography)`
  &:before {
    content: "-";
    padding: 0 4px;
    width: fit-content;
  }
`;
